import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { FirmsService } from 'api';
import {
  FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE,
  FIRM_SUMMARY_QUERY_KEY,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import { GetFirmSummaryParams, GetFirmSummaryReturn, UseGetFirmSummaryParams } from './types';

const getFirmSummary = async (params: GetFirmSummaryParams) => {
  const { firmId, fundId, measurementDate, breakevenMultiple } = params;

  if (isNil(firmId)) {
    return throwError(FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const firmSummary = await FirmsService.apiFirmsSummaryRead(
      toString(firmId),
      measurementDate,
      fundId,
      breakevenMultiple
    );

    if (!firmSummary) {
      return throwError(FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE);
    }

    return firmSummary;
  } catch (error) {
    return throwError(FIRM_SUMMARY_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmSummary = (params: UseGetFirmSummaryParams): GetFirmSummaryReturn => {
  const { firmId, fundId, measurementDate, breakevenMultiple, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFirmSummaryReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && shouldQueryAutomatically,
    queryFn: () => getFirmSummary({ firmId, fundId, measurementDate, breakevenMultiple }),
    queryKey: [FIRM_SUMMARY_QUERY_KEY, firmId, fundId, measurementDate, breakevenMultiple],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmSummary;
